import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BubbleInput, BubbleButton, BubbleAlert } from '@go-client/components/BubbleUI';
import { safeIdCompare, hasOwnProperty } from '@go-shared/lib/utils';
import { mapBy } from '@go-shared/lib/functional';
import { replaceTranslationByRefer, getTranslations } from '@go-client/lib/api/translation';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import Fuse from 'fuse.js';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import TranslationBlock from '@go-client/components/TranslationBlock';
import Translations from './Translations';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  generateDefaultNewTranslation,
  makeMealPeriodTranslationTransform,
  TranslationFilters,
} from '@go-shared/lib/translation';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  MealPeriodTransforms,
  MealPeriodTranslatable,
  MealPeriodTranslation,
} from '@go-shared/types/translation';
import { BrandMealPeriod } from '@go-shared/lib/mealPeriods';
import TranslationsSubNavigation from '../../components/Navigations/TranslationsSubNavigation';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canModifyBrandTranslation } from '@go-shared/lib/permissions';

interface MealPeriodTranslationDataBundle {
  mealPeriods: BrandMealPeriod[];
  mealPeriodTranslations: MealPeriodTranslation[];
  mealPeriodsMap: { [key: string]: BrandMealPeriod };
  mealPeriodTranslationsMap: { [key: string]: MealPeriodTranslation };
}

interface MealPeriodTranslationForm {
  dirty: boolean;
  transforms: MealPeriodTransforms;
}

interface ChangeMealPeriodFormAction {
  active?: boolean;
  valueChange?: { key: keyof MealPeriodTranslatable; value: string };
}

type MealPeriodTranslationsPageProps = RouteComponentProps;

export default function MealPeriodTranslations(props: MealPeriodTranslationsPageProps) {
  const [loading, setLoading] = useState(true);
  const [dataBundle, setDataBundle] = useState<MealPeriodTranslationDataBundle | undefined>(
    undefined
  );
  const [activeMealPeriod, setActiveMealPeriod] = useState<BrandMealPeriod | undefined>(undefined);
  const [mealPeriodTranslationForm, setMealPeriodTranslationForm] = useState<
    MealPeriodTranslationForm | undefined
  >(undefined);
  const [search, setSearch] = useState('');

  const contentRef = useRef<HTMLDivElement | null>(null);

  const { activeProperty } = useActiveProperty();

  const serverInfo = useServerInfo();
  const { brand, languages } = serverInfo;
  const { user } = useUserInfo();

  useEffect(() => {
    setLoading(true);
    fetchDataBundle().catch(console.error);
  }, [activeProperty]);

  // this will refresh the currently active meal period if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;

    setActiveMealPeriod(state => {
      const brandMealPeriod = dataBundle.mealPeriods[0];
      if (!state) return brandMealPeriod;
      const refreshedMealPeriod = dataBundle.mealPeriodsMap[state._id];
      return refreshedMealPeriod || brandMealPeriod;
    });
  }, [dataBundle]);

  // this will reset the form each time a new meal period is clicked
  useEffect(() => {
    resetMealPeriodsForm();
  }, [activeMealPeriod]);

  // this will scroll the page to top when search is modified
  useEffect(() => {
    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [search, contentRef]);

  const fuse = useMemo(() => {
    if (!dataBundle) return undefined;

    return new Fuse(dataBundle.mealPeriods, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [dataBundle]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredMealPeriods = !dataBundle ? [] : fuseSearchResults || dataBundle.mealPeriods;

  async function fetchDataBundle() {
    try {
      const mealPeriod = brand.mealPeriods;
      const mealPeriodTranslations = await getTranslations(activeProperty._id, 'mealperiod');

      const mealPeriodsMap = mapBy(mealPeriod, '_id');
      const mealPeriodTranslationsMap = mapBy(mealPeriodTranslations, 'refers');

      setDataBundle({
        mealPeriods: mealPeriod,
        mealPeriodTranslations,
        mealPeriodsMap,
        mealPeriodTranslationsMap,
      });
    } finally {
      setLoading(false);
    }
  }

  function resetMealPeriodsForm() {
    const transforms = activeMealPeriod
      ? makeMealPeriodTranslationTransform(
          languages.map(l => l.key),
          dataBundle?.mealPeriodTranslationsMap[activeMealPeriod._id]
        )
      : undefined;

    if (!transforms) {
      setMealPeriodTranslationForm(undefined);
      return;
    }

    const formData: MealPeriodTranslationForm = {
      dirty: false,
      transforms,
    };

    setMealPeriodTranslationForm(formData);
  }

  function changeMealPeriodForm(lang: string, change: ChangeMealPeriodFormAction) {
    if (!mealPeriodTranslationForm) return;

    const updatedForm = { ...mealPeriodTranslationForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transforms, lang)) {
      return;
    }

    updatedForm.transforms[lang].modified = true;

    if (change.active !== undefined) updatedForm.transforms[lang].active = change.active;
    if (change.valueChange !== undefined) {
      updatedForm.transforms[lang].delta[change.valueChange.key] = change.valueChange.value;
    }

    setMealPeriodTranslationForm(updatedForm);
  }

  async function handleSubmitMealPeriodForm() {
    if (
      !mealPeriodTranslationForm ||
      !activeMealPeriod ||
      !dataBundle ||
      !canModifyBrandTranslation(user, serverInfo)
    )
      return;

    const translation = generateDefaultNewTranslation(activeMealPeriod._id, 'mealperiod');

    translation.transforms = mealPeriodTranslationForm.transforms;

    await replaceTranslationByRefer(activeMealPeriod._id, translation);
    await fetchDataBundle();
    setMealPeriodTranslationForm({ ...mealPeriodTranslationForm, dirty: false });
  }

  function headerFn() {
    return <TranslationsSubNavigation active="mealPeriods" property={activeProperty} />;
  }

  function leftFn() {
    return (
      <>
        <div
          className="sticky flex bg-white border-b"
          style={{ top: 'calc(130px + 40px + 0.75rem)', height: '50px', zIndex: 3 }}
        >
          <BubbleInput
            type="text"
            block
            appearance="bare"
            placeholder="Search..."
            className="flex-grow"
            inputStyle={{ height: '49px' }}
            inputClassName="px-5"
            value={search}
            onValue={setSearch}
          />
        </div>
        <div style={{ paddingTop: '50px' }}>
          {filteredMealPeriods.map(mealPeriod => (
            <div
              key={mealPeriod._id}
              className={`list-item px-5 py-3 cursor-pointer ${
                activeMealPeriod && safeIdCompare(activeMealPeriod._id, mealPeriod._id)
                  ? 'active'
                  : ''
              }`}
              onClick={() => setActiveMealPeriod(mealPeriod)}
            >
              <div className="flex items-center">
                <div className="overflow-ellipsis">
                  <div className="list-item-name font-semibold">{mealPeriod.name}</div>
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                />
              </div>
            </div>
          ))}
          {filteredMealPeriods.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No meal periods found
            </div>
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    return (
      <>
        {dataBundle && activeMealPeriod && mealPeriodTranslationForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              {!canModifyBrandTranslation(user, serverInfo) && (
                <BubbleAlert className="mt-3 mx-3" variant="ocean">
                  This meal period cannot be modified unless you're in a brand instance and a brand
                  admin.
                </BubbleAlert>
              )}
              <TranslationBlock
                label="Name"
                value={activeMealPeriod.name}
                languages={selectedLanguages}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({mealPeriodTranslationForm.transforms[language.key].delta.name.length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !mealPeriodTranslationForm.transforms[language.key].active
                          ? 'smoke'
                          : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeMealPeriodForm(language.key, {
                          active: !mealPeriodTranslationForm.transforms[language.key].active,
                        })
                      }
                      icon={
                        mealPeriodTranslationForm.transforms[language.key].active
                          ? 'eye'
                          : 'eye-slash'
                      }
                    />
                    <BubbleInput
                      type="text"
                      block
                      className="flex-grow"
                      inputClassName={
                        !mealPeriodTranslationForm.transforms[language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={mealPeriodTranslationForm.transforms[language.key].delta.name}
                      onValue={val =>
                        changeMealPeriodForm(language.key, {
                          valueChange: { key: 'name', value: val },
                        })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
            </div>

            <div className="sticky bottom-0 p-3 bg-eggshell border-t">
              <BubbleButton
                onClick={() => handleSubmitMealPeriodForm()}
                variant="navy"
                disabled={
                  !mealPeriodTranslationForm.dirty || !canModifyBrandTranslation(user, serverInfo)
                }
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!mealPeriodTranslationForm.dirty}
                inverse
                onClick={() => resetMealPeriodsForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if (mealPeriodTranslationForm && mealPeriodTranslationForm.dirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={mealPeriodTranslationForm && mealPeriodTranslationForm.dirty}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        category="mealPeriods"
        loading={loading}
        contentRef={contentRef}
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
      />
    </>
  );
}
